import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  Checkbox,
  CheckboxFontSize,
  FlexColumn,
  FlexRow,
  PageTile,
} from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';
import { GraphComponent } from './graph-component';
import { NothingFound } from '../../../../../src/app/scenes/NothingFoundScene';
import {
  ChartBlock,
  TimeFrameButtonsGroup,
  timeFrameValues,
  TitleContainer,
} from '../chart-components';

import {
  GraphComponentDataFormatType,
  TimeFrameType,
  YAxisRangeStartType,
} from '../chart.types';

export interface TimeValuesChartProps {
  data: GraphComponentDataFormatType[];
  loading: boolean;
  title: string;
  titleDetails?: string | React.ReactNode;
  emptyStateMessage?: string;
  handleChartPeriodChange: (value: number) => void;
  description?: string;
  tooltipFormatter?: (value: any) => string;
  axisLine?: boolean;
  yAxisTickFormatter: (value: any) => string;
  yAxisTick?: (props: any) => React.ReactElement;
  yAxisLabelText?: string;
  yAxisValueKey: string;
  yAxisRangeStart?: YAxisRangeStartType;
}

const customColorsByIndex = {
  0: 'contentGreen',
  1: 'contentLightBlue',
  2: 'contentYellow',
};

const CheckboxListContainer = styled.div(({ theme }) =>
  theme.withMedia({
    marginLeft: [0, 120, 60, 63],
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
    '& > * + *': {
      marginLeft: [24, 32, 32],
    },
    '& >:last-child': {
      marginTop: [28, 0, 0],
      marginLeft: [0, 32, 32],
    },
  })
);

export const TimeValuesChart = (props: TimeValuesChartProps) => {
  const { data, handleChartPeriodChange, title, titleDetails } = props;
  const theme = useTheme();

  const [selectedCheckboxKeys, setSelectedCheckboxKeys] = useState<string[]>(
    []
  );

  const selectedDataKeys = data
    .filter((item) => (item.checkboxLabel ? item.initiallySelected : true))
    .map((item) => item.graphKey);

  useEffect(() => {
    setSelectedCheckboxKeys(selectedDataKeys);
  }, []);

  const [activeTimeFrame, setActiveTimeFrame] = useState<TimeFrameType>(
    timeFrameValues[1]
  );

  const handleCheckboxChange = (key) => {
    if (selectedCheckboxKeys.length === 1 && selectedCheckboxKeys.includes(key))
      return;

    if (selectedCheckboxKeys.includes(key)) {
      setSelectedCheckboxKeys(
        selectedCheckboxKeys.filter((selectedKey) => selectedKey !== key)
      );
    } else {
      setSelectedCheckboxKeys([...selectedCheckboxKeys, key]);
    }
  };

  const handleTimeFrameChange = (timeframe: TimeFrameType) => {
    handleChartPeriodChange(timeframe.days);
    setActiveTimeFrame(timeframe);
  };

  const dataWithMatchingColors = data.map((item, idx) => ({
    ...item,
    themeColor: theme.styleguideColors[customColorsByIndex[idx]],
  }));

  const filteredData = dataWithMatchingColors.filter((item) =>
    selectedCheckboxKeys.includes(item.graphKey)
  );

  const multipleGraphChart = dataWithMatchingColors.length > 1;

  return (
    <PageTile>
      <ChartBlock>
        <TitleContainer>
          <FlexColumn gap={4}>
            <BodyText scale={'xl'} size={1} variation="black" noWrap>
              {title}
            </BodyText>
            {titleDetails}
          </FlexColumn>
          <TimeFrameButtonsGroup
            handleChange={handleTimeFrameChange}
            activeTimeFrame={activeTimeFrame}
          />
        </TitleContainer>

        {filteredData[0]?.data && filteredData[0]?.data?.length > 0 ? (
          <GraphComponent
            {...props}
            graphData={filteredData}
            multipleGraphChart={multipleGraphChart}
            xAxisTickFormatter={activeTimeFrame.formatter}
          />
        ) : (
          <FlexRow justify={'center'} style={{ width: '100%' }}>
            <NothingFound message={props.emptyStateMessage} />
          </FlexRow>
        )}

        {multipleGraphChart && (
          <CheckboxListContainer>
            {dataWithMatchingColors.map((item, idx) => (
              <Checkbox
                key={idx}
                label={item.checkboxLabel}
                checked={selectedCheckboxKeys.includes(item.graphKey)}
                customCheckedColor={item.themeColor}
                customUncheckedColor={theme.styleguideColors.contentTertiary}
                onChange={() => handleCheckboxChange(item.graphKey)}
                checkboxFontSize={CheckboxFontSize.small}
              />
            ))}
          </CheckboxListContainer>
        )}
      </ChartBlock>
    </PageTile>
  );
};
